import { useCallback, useEffect, useState } from 'react';
import {
  formatInrDisplay,
  resolveAmountForDisplay,
} from './calculatorFormatters';

export const CALC_AMOUNT_DISPLAY_MODE_KEY = 'pw-calc-amount-display-mode';
export const CALC_AMOUNT_DISPLAY_MODE_EVENT = 'pw-calc-amount-display-mode';

/** @typedef {'rounded' | 'precise'} CalcAmountDisplayMode */

function readStoredMode() {
  try {
    const v = window.localStorage.getItem(CALC_AMOUNT_DISPLAY_MODE_KEY);
    if (v === 'precise' || v === 'rounded') return v;
  } catch {
    /* ignore */
  }
  return 'rounded';
}

/**
 * Shared rounded/precise preference for calculator monetary outputs.
 * Persists in localStorage so embeds and pages stay in sync.
 */
export function useCalcAmountDisplayMode() {
  const [mode, setModeState] = useState(/** @type {CalcAmountDisplayMode} */ ('rounded'));

  useEffect(() => {
    setModeState(readStoredMode());

    function onStorage(e) {
      if (e.key !== CALC_AMOUNT_DISPLAY_MODE_KEY) return;
      const next = e.newValue === 'precise' ? 'precise' : 'rounded';
      setModeState(next);
    }

    function onCustom(e) {
      const next = e?.detail === 'precise' ? 'precise' : 'rounded';
      setModeState(next);
    }

    window.addEventListener('storage', onStorage);
    window.addEventListener(CALC_AMOUNT_DISPLAY_MODE_EVENT, onCustom);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(CALC_AMOUNT_DISPLAY_MODE_EVENT, onCustom);
    };
  }, []);

  const setMode = useCallback((next) => {
    const m = next === 'precise' ? 'precise' : 'rounded';
    setModeState(m);
    try {
      window.localStorage.setItem(CALC_AMOUNT_DISPLAY_MODE_KEY, m);
    } catch {
      /* ignore */
    }
    window.dispatchEvent(
      new CustomEvent(CALC_AMOUNT_DISPLAY_MODE_EVENT, { detail: m }),
    );
  }, []);

  const toggle = useCallback(() => {
    setMode(mode === 'precise' ? 'rounded' : 'precise');
  }, [mode, setMode]);

  const formatInr = useCallback((value) => formatInrDisplay(value, mode), [mode]);
  const resolveAmount = useCallback(
    (value) => resolveAmountForDisplay(value, mode),
    [mode],
  );

  return {
    mode,
    isPrecise: mode === 'precise',
    setMode,
    toggle,
    formatInr,
    resolveAmount,
  };
}
